import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import useSelector from 'hooks/useSelector';
import { Button } from 'reactstrap';

import { getFatigueAssessmentDetails, fatigueAssessmentAddNote, saveAction } from 'shared/services/fatigueAssessmentApi';
import { formatDateTime } from "shared/models/mapping/mapping";
import FatigueAssessmentPage from './FatigueAssessmentPage';
import FatigueAssessmentQuestions, { FatigueAssessmentQuestion } from './FatigueAssessmentQuestions'
import { alertOptions, yesNoOptions, sexOptions, riskOptions, actionOptions, OtherLocation } from './FormControls'
import FatigueAssessmentAction from './FatigueAssessmentAction'
import './FatigueAssessments.css'


import {
    shiftSummaryDashboardPending,
    shiftSummaryDashboardToAction,
    shiftSummaryDashboardToRevisit,
    shiftSummaryDashboardCompleted
} from "./FatigueAssessmentPageSelector";

import {
    FatigueAssessmentDetailsModel,
    FatigueAssessmentNote,
    FatigueAssessmentUser,
    FatigueAssessmentActionModel,
    SubmitActionModel
} from 'shared/models/api/models';

import {
    fatigueAssessmentDashboardPathRoute,
    dashboardPending,
    dashboardAction,
    dashboardRevisit,
    dashboardCompleted
} from "components/portal/fatigueAssessment/FatigueAssessmentRoutes";

import {
    FatigueAssessmentActionState,
    fatigueAssessmentActionState,
    handleActionChange,
    enableActionValidation,
    resetActionValidation,
    actionStateInvalid
} from './FatigueAssessmentActionState'

import {
    Add as AddIcon,
    Remove as RemoveIcon
} from '@mui/icons-material';
import { ISelectOption } from '../forms/util';


interface TabHeaderProps {
    label: string,
    tabValue: string,
    activeTab: string,
    onClick: any,
    children?: React.ReactNode
}

const TabHeader = (props: TabHeaderProps) => {
    const isTabExpanded = () => {
        return props.activeTab === props.tabValue;
    }
    const tabExpanded = isTabExpanded();

    return (
        <>
            <div className="fa-details-tab-header" onClick={() => props.onClick(props.tabValue)}>
                {props.label}
                <div className="fa-details-tab-header-icon">
                    {tabExpanded ?
                        <RemoveIcon />
                        :
                        <AddIcon />
                    }
                </div>

            </div>
            <div className={`fa-details-tab-content${ tabExpanded ? "" : " d-none" }`}>
                {props.children}
            </div>
        </>
    );
}


export const Tabs = {
    details: 'details',
    info: 'info',
    recommendations: 'recommendations',
    actions: 'actions',
    notes: 'notes',
    audit: 'audit'
}

interface QaRowProps {
    faQuestion: FatigueAssessmentQuestion,
    formDetails: FatigueAssessmentDetailsModel,
    translateAnswer?: any
}

const QaRow = (props: QaRowProps) => {
    function getKeyValue<T>(obj: T, key: string) {
        let keys = key.split('.');
        let value: any = obj;

        keys.forEach(k => {
            if (value == null || !value.hasOwnProperty(k)) {
                return null;
            }
            value = value[k as keyof T];
        });
        return value;
    }

    let keyVal = getKeyValue(props.formDetails, props.faQuestion.key);

    return (
        <div key={keyVal} className="fa-striped-bg p-2">
            <div className="font-weight-bold">{props.faQuestion.altValue ?? props.faQuestion.value}</div>
            <div className="pl-2">{
                props.translateAnswer ?
                    props.translateAnswer(keyVal)
                    :
                    keyVal
            }</div>
        </div>
    );
}


interface TabProps {
    formDetails: FatigueAssessmentDetailsModel
}

const DetailsTab = (props: TabProps) => {
    const q = FatigueAssessmentQuestions;

    const translateAlert = (val: number) => {
        return val != null ? alertOptions[val].label : '';
    }

    const translateYesNo = (val: number) => {
        return val != null ? yesNoOptions.find(x => x.value === val)?.label : '';
    }

    return (
        <>
            <QaRow faQuestion={q.hoursSleep24Hrs} formDetails={props.formDetails} />
            <QaRow faQuestion={q.hoursSleep48Hrs} formDetails={props.formDetails} />
            <QaRow faQuestion={q.currentHoursAwake} formDetails={props.formDetails} />
            <QaRow faQuestion={q.feelAlert} formDetails={props.formDetails} translateAnswer={translateAlert} />
            <QaRow faQuestion={q.alcoholicDrinks} formDetails={props.formDetails} />
            <QaRow faQuestion={q.unfitSubstanceCheck} formDetails={props.formDetails} translateAnswer={translateYesNo} />
            <QaRow faQuestion={q.concentrationCheck} formDetails={props.formDetails} translateAnswer={translateYesNo} />
        </>
    );
}

const InfoTab = (props: TabProps) => {
    const q = FatigueAssessmentQuestions;

    const translateSex = (val: number) => {
        return val != null ? sexOptions[val].label : '';
    }

    const translateRisk = (val: number) => {
        return val != null ? riskOptions[val].label : '';
    }

    const translateLocation = (val: any) => {
        return val ? val : OtherLocation.label;
    }

    return (
        <>
            <QaRow faQuestion={q.user} formDetails={props.formDetails} />
            <QaRow faQuestion={q.gender} formDetails={props.formDetails} translateAnswer={translateSex} />
            <QaRow faQuestion={q.signOnLocation} formDetails={props.formDetails} translateAnswer={translateLocation} />
            {!(props.formDetails.signOnLocation) &&
                <>
                    <QaRow faQuestion={q.supervisor} formDetails={props.formDetails} />
                </>
            }
            <QaRow faQuestion={q.shiftStartTime} formDetails={props.formDetails} translateAnswer={formatDateTime} />
            <QaRow faQuestion={q.assessmentDate} formDetails={props.formDetails} translateAnswer={formatDateTime} />
            {props.formDetails.onBehalfSubmitter?.id &&
                <>
                    <QaRow faQuestion={q.onBehalfSubmitter} formDetails={props.formDetails} />
                    <QaRow faQuestion={q.onBehalfReason} formDetails={props.formDetails} />
                </>
            }
            <QaRow faQuestion={q.risk} formDetails={props.formDetails} translateAnswer={translateRisk} />
        </>
    );
}

const RecommendationsTab = (props: TabProps) => {
    return (
        <>
            <div className="fa-risk-header fa-risk-header-low">Low risk</div>
            <div className="fa-risk-section">
                <div className="fa-risk-subsection">
                    <div>Individuals are:</div>
                    <ul>
                        <li>Alert</li>
                        <li>Normal eye blinks (less than 1 second)</li>
                        <li>Coordinated body movements</li>
                        <li>Tolerant of others</li>
                    </ul>
                </div>
                <div className="fa-risk-subsection">
                    <div className="font-weight-bold">Recommendation</div>
                    <ul>
                        <li>Continue to monitor</li>
                        <li>Remind individuals about fatigue and alertness management strategies (interaction with others, coffee, exercise, cold air on face, etc.)</li>
                    </ul>
                </div>
            </div>

            <div className="fa-risk-header fa-risk-header-medium">Medium risk</div>
            <div className="fa-risk-section">
                <div className="fa-risk-subsection">
                    <div>The individual reports they are fatigued and/or are showing some of the following signs:</div>
                    <ul>
                        <li>Irritable/impatient</li>
                        <li>Longer eyelid closure (1-2 seconds)</li>
                        <li>Wandering thoughts</li>
                        <li>Rubs eyes or face</li>
                        <li>Facial contortions</li>
                        <li>Restless movements</li>
                        <li>Yawning</li>
                    </ul>
                </div>
                <div className="fa-risk-subsection">
                    <div className="font-weight-bold">Recommendation</div>
                    <div>As above, plus:</div>
                    <ul>
                        <li>Discuss possible reasons for fatigue</li>
                        <li>Rotate tasks</li>
                        <li>Encourage the use of alertness strategies</li>
                        <li>Provide opportunity for a short break/brief nap of no more than 15 minutes</li>
                        <li>Have personnel work together (if possible)</li>
                        <li>Remove from safety sensitive work</li>
                        <li>Assess fitness for work before you allow person to return to work</li>
                        <li>Schedule regular supervision for remainder of shift </li>
                    </ul>
                </div>
            </div>

            <div className="fa-risk-header fa-risk-header-high">High risk</div>
            <div className="fa-risk-section">
                <div className="fa-risk-subsection">
                    <div>The individual reports they are significantly fatigued and/or are showing some of the following serious signs:</div>
                    <ul>
                        <li>Quiet and withdrawn</li>
                        <li>Long eyelid closure (2 or more seconds)</li>
                        <li>Fixed staring</li>
                        <li>Frequent yawning</li>
                        <li>Micro sleeps</li>
                    </ul>
                </div>
                <div className="fa-risk-subsection">
                    <div className="font-weight-bold">Recommendation</div>
                    <ul>
                        <li>Immediately prevent person from working and discuss the possible causes and action required</li>
                        <li>Determine if the individual can be placed on alternate duties for the remainder of shift and managed at work</li>
                        <li>If unable to be managed on aternate duties, send the individual home (provide transportation) and report event in 1SAP</li>
                    </ul>
                </div>
            </div>
        </>
    );
}

interface ActionsTabProps extends TabProps {
    reload: (value: boolean) => any;
}

const ActionsFormTab = (props: ActionsTabProps) => {
    const [action, setAction] = useState({} as SubmitActionModel);
    const profile = useSelector(state => state.profile);

    const save = () => {
        enableActionValidation();

        let conditionalFields = [] as string[][];
        if (isAlternativeSupervisor()) {
            conditionalFields.push(["alternativeSupervisorReason"]);
        }

        if (actionStateInvalid(conditionalFields) > 0) {
            return;
        }

        if (!action.fatigueAssessmentId) {
            return;
        }
        saveAction(action)
            .then(saveResults);
    }

    const saveResults = (result: any) => {
        resetActionValidation();

        if (result > 0) {
            props.reload(true);
        }
    }

    const isAlternativeSupervisor = () => {
        if (props.formDetails.supervisor?.id !== profile?.id) {
            return true;
        }
        return false;
    }

    useEffect(() => {
        const sub = fatigueAssessmentActionState.subscribe((s: FatigueAssessmentActionState) => {
            setAction(s.action);
        });
        handleActionChange("fatigueAssessmentId")(props.formDetails.id);
        return () => { sub.unsubscribe(); };
    }, []);

    return (
        <>
            <div className="main-form-row fa-section-header pt-3">Supervisors - Action taken</div>
            <FatigueAssessmentAction alternativeSupervisor={isAlternativeSupervisor()} />
            <div className="main-form-row">
                <Button className="btn-primary m-1" onClick={save}>Save details</Button>
            </div>
        </>
    );
}

const ActionsInfoTab = (props: TabProps) => {
    const q = FatigueAssessmentQuestions;

    const translateActions = (action: FatigueAssessmentActionModel) => {
        let actions = [] as string[];

        if (!action) {
            return actions;
        }

        if (action.noFurtherAction) actions.push(actionOptions.noFurtherAction);
        if (action.noActionTakenPlannedLeave) actions.push(actionOptions.noActionTakenPlannedLeave);
        if (action.noActionTakenUnplannedLeave) actions.push(actionOptions.noActionTakenUnplannedLeave);
        if (action.actionTaken) actions.push(actionOptions.actionTaken);
        if (action.actionTakenRevisit) actions.push(actionOptions.actionTakenRevisit);
        if (action.actionTakenBuddyUp) actions.push(actionOptions.actionTakenBuddyUp);
        if (action.actionTakenRemoveFromSafetyCritical) actions.push(actionOptions.actionTakenRemoveFromSafetyCritical);
        if (action.actionTakenRemainLocal) actions.push(actionOptions.actionTakenRemainLocal);
        if (action.actionTakenAltDuties) actions.push(actionOptions.actionTakenAltDuties);
        if (action.sentHome) actions.push(actionOptions.sentHome);


        return (
            <>
                {actions.map((actionDescription) =>
                    <div>{actionDescription}</div>
                )}
            </>
        );
    }

    const translateBoolToYesNo = (val: boolean) => {
        return val != null ? yesNoOptions.find(x => x.value === +val)?.label : '';
    }

    const translateUserList = (val: FatigueAssessmentUser[]) => {
        return (
            <>
                {val != null && val.map((user) =>
                    <div key={user.id}>
                        {user.fullname}
                    </div>
                )}
            </>
        );
    }
    return (
        <>
            <QaRow faQuestion={q.controlsImplementedBy} formDetails={props.formDetails} />
            <QaRow faQuestion={q.actionTakenGroup} formDetails={props.formDetails} translateAnswer={translateActions} />
            <QaRow faQuestion={q.actionComment} formDetails={props.formDetails} />
            <QaRow faQuestion={q.actionConfidential} formDetails={props.formDetails} translateAnswer={translateBoolToYesNo} />
            <QaRow faQuestion={q.actionSupervisors} formDetails={props.formDetails} translateAnswer={translateUserList} />
        </>
    );
}


const ActionsTab = (props: ActionsTabProps) => {
    return (
        <>
            {props.formDetails.action ?
                <ActionsInfoTab formDetails={props.formDetails} />
                :
                <ActionsFormTab formDetails={props.formDetails} reload={props.reload} />
            }
        </>
    );
}

const NotesTab = (props: TabProps) => {
    const [notes, setNotes] = useState(props.formDetails.notes);
    const [noteText, setNoteText] = useState('');
    const [saving, setSaving] = useState(false);

    const maxNoteLength = 500;

    const addNote = () => {
        if (!noteText || noteText.length < 1) {
            return;
        }

        setSaving(true);

        fatigueAssessmentAddNote(
            {
                fatigueAssessmentId: props.formDetails.id,
                comment: noteText
            }
        )
            .then(newNotes => addNoteResult(newNotes));
    }

    const addNoteResult = (newNotes: FatigueAssessmentNote[] | null) => {
        if (!newNotes) {
            return;
        }
        setNotes(newNotes);
        setNoteText('');
        setSaving(false);
    }

    useEffect(() => {
        if (noteText.length > maxNoteLength) {
            setNoteText(noteText.substring(0, maxNoteLength));
        }
    }, [noteText]);

    return (
        <>
            {notes.length > 0 &&
                <div>
                    <div className="fa-section-header">Previous notes</div>
                    <div>
                        {
                            notes.map((note) => (
                                <div key={note.id} className="fa-striped-bg p-2">
                                    <div className="fa-note-name">{note.userName}</div>
                                    <div className="fa-note-date">{formatDateTime(note.noteDate)}</div>
                                    <div className="fa-note-comment">{note.comment}</div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            }
            <div className="fa-section-header">Add notes</div>
            <div className="whole-text-area">
                <div className="whole-text-area-disabled"></div>
                <div className="pt-3 pb-2">
                    <textarea
                        className="fa-multiline-input"
                        placeholder={`Enter comments here (max. ${ maxNoteLength } characters)`}
                        maxLength={maxNoteLength}
                        rows={5}
                        value={noteText}
                        disabled={saving}
                        onChange={((event) => setNoteText(event.target.value))} />
                </div>
                <div>
                    {saving ?
                        <Button className="btn-primary m-1 disabled">Saving...</Button>
                        :
                        <Button className="btn-primary m-1" onClick={addNote}>Add note</Button>
                    }
                </div>
            </div>
        </>
    );
}

const AuditTab = (props: TabProps) => {

    return (
        <>
            {props.formDetails.audit.length > 0 &&
                <div>
                    <div className="fa-section-header">Audit Trail</div>
                    <div>
                        {
                            props.formDetails.audit.map((auditItem) => (
                                <div key={auditItem.id} className="fa-striped-bg p-2">
                                    <div className="fa-audit-date">{formatDateTime(auditItem.date)}</div>
                                    <div className="fa-audit-text">{auditItem.description}</div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            }
        </>
    );
}

const FatigueAssessmentDetails = () => {
    const pages: { [id: string]: ISelectOption; } = {};
    pages[dashboardPending] = shiftSummaryDashboardPending;
    pages[dashboardAction] = shiftSummaryDashboardToAction;
    pages[dashboardRevisit] = shiftSummaryDashboardToRevisit;
    pages[dashboardCompleted] = shiftSummaryDashboardCompleted;

    const params = useParams() as any;
    const [selectedPage] = useState(pages[params.dashboardType]);
    const [formId] = useState(parseInt(params.id));
    const [activeTab, setActiveTab] = useState(params.tab as string);
    const [formDetails, setFormDetails] = useState({} as FatigueAssessmentDetailsModel);
    const [reload, setReload] = useState(false);

    const navigate = useNavigate();

    const getActionPath = (id: number, tab: string) => {
        let url = `${ fatigueAssessmentDashboardPathRoute.path }/${ params.dashboardType }/${ id }/${ tab }`;
        return url;
    }

    const tabClicked = (tab: string) => {
        if (tab === activeTab) {
            tab = '';
        }
        setActiveTab(tab);
        navigate(getActionPath(formId, tab));
    }

    const goToDashboard = () => {
        let url = `${ fatigueAssessmentDashboardPathRoute.path }/${ params.dashboardType }`;
        navigate(url);
    }

    const isOverdue30Min = (shiftStartDate?: Date): boolean => {

        if (shiftStartDate) {
            if (typeof (shiftStartDate) === 'string') {
                shiftStartDate = new Date(shiftStartDate);
            }
            let wellOverdueThreshold = 30 * 60000; //30 minutes
            return (shiftStartDate.getTime() < Date.now() - wellOverdueThreshold);
        }
        return false;
    }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            let results = {} as FatigueAssessmentDetailsModel;
            if (params.id > 0) {
                results = await getFatigueAssessmentDetails(params.id);
            }
            if (mounted && results.id) {
                setFormDetails(results);
            }
        };
        mount();
        return () => { mounted = false };
    }, [params.id]);


    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            let results = {} as FatigueAssessmentDetailsModel;
            if (params.id > 0) {
                results = await getFatigueAssessmentDetails(params.id);
            }
            if (mounted && results.id) {
                setFormDetails(results);
            }
            setReload(false);
        };
        if (reload) {
            mount();
        }
        return () => { mounted = false };
    }, [reload]);

    return (
        <FatigueAssessmentPage key={selectedPage.value} selectedPage={selectedPage}>
            {formDetails && formDetails.id ?
                <>
                    <div className="fa-details-container fa-details-body">

                        <TabHeader
                            label="Details"
                            onClick={tabClicked}
                            tabValue={Tabs.details}
                            activeTab={activeTab}>
                            <DetailsTab formDetails={formDetails} />
                        </TabHeader>

                        <TabHeader
                            label="User Data"
                            onClick={tabClicked}
                            tabValue={Tabs.info}
                            activeTab={activeTab}>
                            <InfoTab formDetails={formDetails} />
                        </TabHeader>

                        <TabHeader
                            label="Recommendations"
                            onClick={tabClicked}
                            tabValue={Tabs.recommendations}
                            activeTab={activeTab}>
                            <RecommendationsTab formDetails={formDetails} />
                        </TabHeader>

                        {(formDetails.assessmentDate || isOverdue30Min(formDetails.shiftStartTime)) &&
                            <TabHeader
                                label="Action"
                                onClick={tabClicked}
                                tabValue={Tabs.actions}
                                activeTab={activeTab}>
                                <ActionsTab formDetails={formDetails} reload={(val) => setReload(val)} />
                            </TabHeader>
                        }

                        <TabHeader
                            label="Notes"
                            onClick={tabClicked}
                            tabValue={Tabs.notes}
                            activeTab={activeTab}>
                            <NotesTab formDetails={formDetails} />
                        </TabHeader>

                        <TabHeader
                            label="History"
                            onClick={tabClicked}
                            tabValue={Tabs.audit}
                            activeTab={activeTab}>
                            <AuditTab formDetails={formDetails} />
                        </TabHeader>

                    </div>
                    <div className="fa-details-container fa-details-footer">
                        <Button className="btn-primary m-1" onClick={goToDashboard}>Back to dashboard</Button>
                    </div>
                </>
                :
                <div>Loading...</div>
            }
        </FatigueAssessmentPage>
    );
}

export default FatigueAssessmentDetails;
